import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"
import {
  platformAddOnRevisionV1Schema,
  platformPlanRevisionV1Schema,
} from "./platform-entitlement-v1.js"

export const platformSubscriptionStatusV1Schema = z.enum([
  "trialing",
  "active",
  "past_due",
  "restricted",
  "canceled",
])

export const platformCollectionsStatusV1Schema = z.enum([
  "current",
  "dunning",
  "restricted",
  "closed",
])

export const platformInvoiceStatusV1Schema = z.enum([
  "draft",
  "open",
  "paid",
  "void",
  "uncollectible",
])

export const platformEntitlementGateV1Schema = z.object({
  mode: z.enum(["full", "restricted", "canceled"]),
  productAccessAllowed: z.boolean(),
  exportAccessAllowed: z.literal(true),
  supportAccessAllowed: z.literal(true),
  paymentHistoryAccessAllowed: z.literal(true),
  preservesTenantData: z.literal(true),
}).strict().superRefine((gate, context) => {
  if (gate.productAccessAllowed !== (gate.mode === "full")) {
    context.addIssue({
      code: "custom",
      path: ["productAccessAllowed"],
      message: "Product access must match the entitlement gate mode.",
    })
  }
})

export const platformBillingReconciliationInputV1Schema = z.object({
  contractVersion: z.literal(1),
  provider: z.literal("stripe_billing"),
  providerEventId: z.string().regex(/^evt_[A-Za-z0-9]{8,64}$/),
  providerEventCreatedAtEpoch: z.number().int().nonnegative(),
  providerEventType: z.enum([
    "customer.subscription.created",
    "customer.subscription.updated",
    "customer.subscription.deleted",
    "invoice.paid",
    "invoice.payment_failed",
    "invoice.voided",
    "invoice.marked_uncollectible",
    "credit_note.created",
    "credit_note.updated",
    "credit_note.voided",
  ]),
  providerCustomerRef: z.string().regex(/^cus_[A-Za-z0-9]{8,64}$/),
  providerSubscriptionRef: z.string().regex(/^sub_[A-Za-z0-9]{8,64}$/),
  providerObjectRef: z.string().regex(/^[a-z]+_[A-Za-z0-9]{8,128}$/),
  targetTenantId: tenantIdSchema,
  subscriptionStatus: platformSubscriptionStatusV1Schema,
  collectionsStatus: platformCollectionsStatusV1Schema,
  invoiceStatus: platformInvoiceStatusV1Schema.nullable(),
  creditBalanceMinor: z.number().int().min(-1_000_000_000).max(1_000_000_000),
  currency: z.literal("usd"),
  plan: platformPlanRevisionV1Schema,
  addOns: z.array(platformAddOnRevisionV1Schema).max(32),
  entitlementGate: platformEntitlementGateV1Schema,
  providerObservedAt: z.iso.datetime({ offset: true }),
  payloadHash: z.string().regex(/^[a-f0-9]{64}$/),
  signatureVerified: z.literal(true),
  platformNamespaceOnly: z.literal(true),
  livemode: z.literal(false),
  correlationId: z.uuid(),
}).strict()

export const platformBillingReconciliationResultV1Schema = z.object({
  responseVersion: z.literal(1),
  targetTenantId: tenantIdSchema,
  subscriptionVersion: z.number().int().positive(),
  subscriptionStatus: platformSubscriptionStatusV1Schema,
  collectionsStatus: platformCollectionsStatusV1Schema,
  entitlementGate: platformEntitlementGateV1Schema,
  disposition: z.enum([
    "applied",
    "replayed",
    "ignored_out_of_order",
    "needs_authoritative_poll",
  ]),
  providerEventId: z.string().regex(/^evt_[A-Za-z0-9]{8,64}$/),
  providerEventCreatedAtEpoch: z.number().int().nonnegative(),
  preservesTenantData: z.literal(true),
}).strict()

export type PlatformSubscriptionStatusV1 = z.infer<
  typeof platformSubscriptionStatusV1Schema
>
export type PlatformBillingReconciliationInputV1 = z.infer<
  typeof platformBillingReconciliationInputV1Schema
>
export type PlatformBillingReconciliationResultV1 = z.infer<
  typeof platformBillingReconciliationResultV1Schema
>
